import React, { useCallback, useEffect, useState } from 'react';
import { NavigationContainer, NavigationIndependentTree, } from '@react-navigation/native';
import { StyleSheet, View, StatusBar, Keyboard } from 'react-native';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectFeatureFlag } from 'reducers/selectors';
import { useNativeStackScreenOptions } from 'hooks/navigation/useStackNavigatorScreenOptions';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { selectSelectedSpace, selectSelectedSpaceData, } from 'features/spaces/reducers/selectors';
import { SuccessScreen } from 'design-system';
import { setShowSpaceLock } from 'features/spaces/actions';
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withTiming, } from 'react-native-reanimated';
import { useIsSignedInWeb } from 'features/web/hooks/useIsSignedInWeb';
import * as spaces from 'features/spaces';
import EmmaProModal from 'features/premium/screens/EmmaProModal';
import { isWeb } from '../../../constants';
const Wrapper = () => {
    const { isSignedIn } = useIsSignedInWeb();
    const isLoggedIn = useAppSelector((store) => store.user.isLoggedIn);
    const isWaiting = useAppSelector((store) => store.user.isWaiting);
    const noWaiting = useAppSelector((store) => selectFeatureFlag(store, 'no_waiting').value);
    if (!(isLoggedIn || isSignedIn) || (isWaiting && !noWaiting))
        return null;
    return <SpaceUnavailableLock />;
};
const SpaceUnavailableLock = () => {
    const showSpaceLock = useAppSelector((store) => store.spaces.showSpaceLock);
    const dispatch = useAppDispatch();
    const opacitySharedValue = useSharedValue(0);
    const [isVisible, setVisible] = useState(showSpaceLock);
    const selectedSpace = useAppSelector(selectSelectedSpaceData);
    useEffect(() => {
        if (selectedSpace &&
            !(selectedSpace?.isDefault || selectedSpace?.isPremium)) {
            dispatch(setShowSpaceLock(true));
        }
    }, [selectedSpace, selectedSpace?.isDefault, selectedSpace?.isPremium]);
    const [isFirstRoute, setIsFirstRoute] = useState(true);
    const onStateChange = useCallback((state) => {
        setIsFirstRoute(state?.index === 0);
    }, []);
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (isFirstRoute &&
                (selectedSpace?.isDefault || selectedSpace?.isPremium)) {
                dispatch(setShowSpaceLock(false));
            }
        }, 300);
        return () => clearTimeout(timeout);
    }, [isFirstRoute, selectedSpace?.isDefault, selectedSpace?.isPremium]);
    useEffect(() => {
        if (showSpaceLock) {
            Keyboard.dismiss();
            setVisible(true);
            opacitySharedValue.value = withTiming(1, { duration: 300 });
        }
        else {
            opacitySharedValue.value = withTiming(0, { duration: 300 }, () => {
                runOnJS(setVisible)(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSpaceLock]);
    const opacityStyle = useAnimatedStyle(() => ({ opacity: opacitySharedValue.value }), [opacitySharedValue]);
    if (!isVisible)
        return null;
    return (<Animated.View style={[styles.view, opacityStyle]}>
      <NavigationIndependentTree>
        <NavigationContainer documentTitle={{
            formatter: (options, route) => `${options?.title || route?.name} - Emma App`,
        }} onStateChange={onStateChange}>
          <View style={styles.container}>
            <StatusBar translucent backgroundColor="transparent" hidden={false} animated/>
            <Navigator />
          </View>
        </NavigationContainer>
      </NavigationIndependentTree>
    </Animated.View>);
};
const SpaceUnavailableStack = createNativeStackNavigator();
const Navigator = () => {
    const options = useNativeStackScreenOptions();
    const id = useAppSelector(selectSelectedSpace);
    return (<SpaceUnavailableStack.Navigator screenOptions={options} id="SpaceUnavailableStack">
      <SpaceUnavailableStack.Screen name="SpaceUnavailable" component={spaces.SpaceUnavailableScreen} initialParams={{ id }}/>
      <SpaceUnavailableStack.Screen name="SpaceUnavailableMoveAccounts" component={spaces.SpaceUnavailableMoveAccountsScreen}/>
      <SpaceUnavailableStack.Screen name="SpaceUnavailableSelectMoveAccounts" component={spaces.SpaceUnavailableSelectMoveAccountsScreen}/>
      <SpaceUnavailableStack.Screen name="SpaceUnavailableAccountsMoved" component={spaces.SpaceUnavailableAccountsMovedScreen}/>
      <SpaceUnavailableStack.Screen name="SpaceUnavailableLeaveAccounts" component={spaces.SpaceUnavailableLeaveAccountsScreen}/>
      <SpaceUnavailableStack.Screen name="Success" component={SuccessScreen}/>
      {!isWeb && (<SpaceUnavailableStack.Screen name="EmmaProModal" component={EmmaProModal}/>)}
    </SpaceUnavailableStack.Navigator>);
};
const styles = StyleSheet.create({
    view: {
        ...StyleSheet.absoluteFillObject,
        zIndex: 2,
    },
    container: {
        flex: 1,
    },
});
export default Wrapper;
